import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useRouteLoaderData } from '@remix-run/react';
import type { TRSSFeedElement } from '@shared/dream-components';
import { RSSFeedElement } from '@shared/dream-components';

import Text from '~/components/Text';
import useIsMobile from '~/hooks/useIsMobile';
import useRssFeed from '~/hooks/useRssFeed';
import type { RootLoaderData } from '~/root';

import { useRssFeedData } from './hooks/useRssFeedData';

import { processRSSFeedNode } from './utils/processors';

interface Props {
  node: TRSSFeedElement;
  children: ReactNode | ReactNode[];
}

const ErrorBoundaryFallback = ({ message }: { message: string }) => (
  <div className="flex max-w-2xl flex-col space-y-2 py-8">
    <Text as="p" type="body" size="md" className="text-center leading-tight tracking-normal">
      {message}
    </Text>
  </div>
);

const RSSFeedBase = ({ node, children }: Props) => {
  const isViewportMobile = useIsMobile();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  const {
    data: rssFeed,
    isLoading,
    isError,
  } = useRssFeed({
    url: node.attrs.url || '',
  });

  const data = useRssFeedData(node, rssFeed);
  const processedNode = processRSSFeedNode(node, { site });

  return (
    <RSSFeedElement
      element={{
        ...processedNode,
        type: 'rss_feed',
        attrs: {
          ...processedNode.attrs,
          data,
          isLoading: isLoading,
          isError,
        },
        content: [{ id: 'empty-children', text: '' }],
      }}
      isLive
      rssFeedMap={site?.viewable_site_version?.customization?.rss_feed_map}
      viewport={isViewportMobile ? 'mobile' : 'desktop'}
    >
      {children}
    </RSSFeedElement>
  );
};

const RSSFeed = ({ node, children }: Props) => {
  return (
    <ErrorBoundary fallbackRender={(props) => <ErrorBoundaryFallback message={props.error.message} />}>
      <RSSFeedBase node={node}>{children}</RSSFeedBase>
    </ErrorBoundary>
  );
};

export default RSSFeed;
